import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authFetch, isLoggedIn } from "../../auth";

const PermissionContext = createContext({
  loading: false,
  initialized: false,
  grants: [],
  can: () => false,
  refresh: async () => {}
});

export function PermissionProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [grants, setGrants] = useState([]);

  async function refresh() {
    if (!isLoggedIn()) {
      setGrants([]);
      setInitialized(true);
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch("/api/permissions/me");
      if (!res.ok) {
        setGrants([]);
        return;
      }
      const data = await res.json();
      setGrants(Array.isArray(data?.grants) ? data.grants : []);
    } catch {
      setGrants([]);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }

  useEffect(() => {
    refresh();
    const handler = () => {
      setInitialized(false);
      refresh();
    };
    window.addEventListener("auth-changed", handler);
    return () => window.removeEventListener("auth-changed", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grantSet = useMemo(() => new Set(grants), [grants]);

  const value = useMemo(() => ({
    loading,
    initialized,
    grants,
    refresh,
    can: (key) => grantSet.has(key)
  }), [loading, initialized, grants, grantSet]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  return useContext(PermissionContext);
}
